import { RegistrationFlow, UpdateRegistrationFlowBody } from '@ory/client'
import { isUiNodeInputAttributes, UserAuthCard } from '@ory/elements'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sdk, useSdkError } from '../services/sdk'
import AuthLayout from '../components/layout/AuthLayout'
import { Button, SpinnerIcon } from '@sistemiv/s-components'
import PrivacyPolicyModal from './PrivacyPolicyModal'
import TermsOfUseModal from './TermsOfUseModal'

/** Registration is a React component that renders the Registration form using Ory Elements.
 * It is used to handle the registration flow for a variety of authentication methods.
 *
 * The Registration component also handles the OAuth2 registration flow (as an OAuth2 provider)
 * For more information regarding OAuth2 registration, please see the following documentation:
 * https://www.ory.sh/docs/oauth2-oidc/custom-login-consent/flow
 *
 */
export const Registration = () => {
  const [flow, setFlow] = useState<RegistrationFlow | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const [openPrivacyPolicyModal, setOpenPrivacyPolicyModal] = useState(false)
  const [openTermsOfUseModal, setOpenTermsOfUseModal] = useState(false)

  const handlePrivacypolicyClick = () => {
    setOpenPrivacyPolicyModal(true)
  }

  const handleTermsOfUseClick = () => {
    setOpenTermsOfUseModal(true)
  }

  // The return_to is a query parameter is set by you when you would like to redirect
  // the user back to a specific URL after registration is successful
  // In most cases it is not necessary to set a return_to if the UI business logic is
  // handled by the SPA.
  // In OAuth flows this value might be ignored in favor of keeping the OAuth flow
  // intact between multiple flows (e.g. Login -> Recovery -> Settings -> OAuth2 Consent)
  // https://www.ory.sh/docs/oauth2-oidc/identity-provider-integration-settings
  const returnTo = searchParams.get('return_to')

  // The login_challenge is a query parameter set by the Ory OAuth2 registration flow
  // Switching between pages should keep the login_challenge in the URL so that the
  // OAuth flow can be completed upon completion of another flow (e.g. Login).
  const loginChallenge = searchParams.get('login_challenge')

  const navigate = useNavigate()

  // Get the flow based on the flowId in the URL (.e.g redirect to this page after flow initialized)
  const getFlow = useCallback(
    (flowId: string) =>
      sdk
        // the flow data contains the form fields, error messages and csrf token
        .getRegistrationFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    // eslint-disable-next-line
    [],
  )

  // initialize the sdkError for generic handling of errors
  const sdkErrorHandler = useSdkError(getFlow, setFlow, '/registration', true)

  // create a new registration flow
  const createFlow = () => {
    sdk
      // we don't need to specify the return_to here since we are building an SPA. In server-side browser flows we would need to specify the return_to
      .createBrowserRegistrationFlow({
        ...(returnTo && { returnTo: returnTo }),
        ...(loginChallenge && { loginChallenge: loginChallenge }),
      })
      .then(({ data: flow }) => {
        // Update URI query params to include flow id
        setSearchParams({ ['flow']: flow.id }, { replace: true })
        // Set the flow data
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }

  // submit the registration form data to Ory
  const submitFlow = (body: UpdateRegistrationFlowBody) => {
    // something unexpected went wrong and the flow was not set
    if (!flow) return navigate('/registration', { replace: true })

    sdk
      .updateRegistrationFlow({
        flow: flow.id,
        updateRegistrationFlowBody: body,
      })
      .then(({ data }) => {
        if ('continue_with' in data) {
          for (const cw of data.continue_with ?? []) {
            if (cw.action === 'show_verification_ui') {
              const search = new URLSearchParams()
              search.set('flow', cw.flow.id)
              navigate(
                {
                  pathname: '/verification',
                  search: search.toString(),
                },
                { replace: true },
              )
              return
            }
          }
        }

        // we successfully submitted the login flow, so lets redirect to the dashboard
        navigate('/', { replace: true })
      })
      .catch(sdkErrorHandler)
  }

  useEffect(() => {
    // we might redirect to this page after the flow is initialized, so we check for the flowId in the URL
    const flowId = searchParams.get('flow')
    // the flow already exists
    if (flowId) {
      getFlow(flowId).catch(createFlow) // if for some reason the flow has expired, we need to get a new one
      return
    }
    // we assume there was no flow, so we create a new one
    createFlow()
    // eslint-disable-next-line
  }, [navigate])

  // the flow is not set yet, so we show a loading indicator
  return (
    <AuthLayout>
      {flow ? (
        // create a registration form that dynamically renders based on the flow data using Ory Elements
        <>
          <UserAuthCard
            flowType={'registration'}
            className='custom-auth-card !min-w-[calc(100vw-4rem)] sm:!min-w-[400px] p-4 sm:p-6 rounded-md sm:rounded-lg'
            flow={{
              ...flow,
              ui: {
                ...flow.ui,
                nodes: flow.ui.nodes.filter(
                  (node) =>
                    isUiNodeInputAttributes(node.attributes) &&
                    ![
                      'traits.company',
                      'traits.job',
                      'traits.address.street',
                      'traits.address.state',
                      'traits.address.postalCode',
                      'traits.address.city',
                    ].includes(node.attributes.name),
                ),
                // ⬇⬇⬇ Dodajemo custom poruku o formatu lozinke
                messages: [
                  ...(flow.ui.messages || []),
                  {
                    id: 9999,
                    text: '*Password must be minimum 8 characters long.',
                    type: 'info',
                    context: { identifier: 'password' },
                  },
                ],
              },
            }}
            additionalProps={{
              loginURL: {
                handler: () => {
                  const search = new URLSearchParams()
                  if (flow.return_to) search.set('return_to', flow.return_to)
                  if (flow.oauth2_login_challenge) search.set('login_challenge', flow.oauth2_login_challenge)
                  navigate({ pathname: '/login', search: search.toString() }, { replace: true })
                },
              },
            }}
            includeScripts={true}
            onSubmit={({ body }) => submitFlow(body as UpdateRegistrationFlowBody)}
          />
          <div className='flex items-center justify-center gap-x-3 text-gray-500'>
            <Button onClick={handlePrivacypolicyClick}>Privacy policy</Button>{' '}
            <Button onClick={handleTermsOfUseClick}>Terms of use</Button>
          </div>
          {openPrivacyPolicyModal && (
            <PrivacyPolicyModal open={openPrivacyPolicyModal} setOpen={setOpenPrivacyPolicyModal} />
          )}
          {openTermsOfUseModal && <TermsOfUseModal open={openTermsOfUseModal} setOpen={setOpenTermsOfUseModal} />}
        </>
      ) : (
        <SpinnerIcon className='w-6 h-6 text-sky-500 animate-spin' />
      )}
    </AuthLayout>
  )
}
